import React, { useEffect, useRef, useState } from "react"
import styles from "./AppSpace.module.scss"
import { UserInterface } from "../../App"
import axios from "axios"
import {
    API_ROUTE,
    frenchSample,
    generateTextFromSpreadsheet,
    imageSample,
    russianSample,
} from "../../utils"
import { Editor } from "@tinymce/tinymce-react"
import Excel from "./Excel/Excel"

const AppSpace: React.FC<{
    setRawContext: any
    activeSession: any
    activeAppSpace: any
    appSpaces: any
    user: UserInterface
}> = ({ setRawContext, activeSession, activeAppSpace, appSpaces, user }) => {
    const [appSpaceContent, setAppSpaceContent] = useState("")
    const [type, setType] = useState("")
    const [, setUnsavedChanges] = useState(false)
    const [appSpaceLoading, setAppSpaceLoading] = useState(false)

    const editorRef = useRef(null as any)
    const saveTimeoutRef = useRef<NodeJS.Timeout>()

    const saveChanges = (newContent: any) => {
        axios.post(`${API_ROUTE}/update-file`, {
            fileId: activeAppSpace.fileId,
            content:
                activeAppSpace.type === "docx"
                    ? newContent
                    : `data:text/plain;base64,${btoa(newContent)}`,
        })

        setUnsavedChanges(false)
    }

    const debouncedSaveChanges = () => {
        if (saveTimeoutRef.current) {
            clearTimeout(saveTimeoutRef.current)
        }
        saveTimeoutRef.current = setTimeout(() => {
            saveChanges(editorRef.current.getContent())
        }, 1500) // Save after inactivity
    }

    useEffect(() => {
        ;(async () => {
            if (!activeAppSpace.fileId || !activeAppSpace.type) return
            setAppSpaceLoading(true)
            setType("none")

            await new Promise((resolve) => setTimeout(resolve, 200))

            if (activeAppSpace.fileId === "french") {
                setRawContext(frenchSample)
                setAppSpaceContent(frenchSample)
                setType("text")
                return
            } else if (activeAppSpace.fileId === "russian") {
                setRawContext(russianSample)
                setAppSpaceContent(russianSample)
                setType("text")
                return
            } else if (activeAppSpace.fileId === "image") {
                setRawContext(imageSample)
                setAppSpaceContent(imageSample)
                setType("image")
                return
            } else if (activeAppSpace.fileId === "sheet") {
                // setRawContext("this is a spreadsheet")
                // setAppSpaceContent("{}")
                // setType("sheet")
                return
            }

            const response = await axios.get(`${API_ROUTE}/get-file/${activeAppSpace.fileId}`)
            const dataUrl = response.data.content

            if (activeAppSpace.type === "application/pdf") {
                const text = await axios.post(`${API_ROUTE}/utils/pdf`, { url: dataUrl })
                setRawContext(text.data)
                setAppSpaceContent(dataUrl)
                setType("pdf")
                return
            } else if (activeAppSpace.type.startsWith("image")) {
                setRawContext(dataUrl)
                setAppSpaceContent(dataUrl)
                setType("image")
                return
            } else if (activeAppSpace.type === "docx") {
                setRawContext(dataUrl)
                setAppSpaceContent(dataUrl)
                setType("text")
            } else if (activeAppSpace.type === "sheet") {
                fetch(dataUrl)
                    .then((res) => res.text())
                    .then((text) => {
                        setType("sheet")
                        setAppSpaceContent(text)

                        const betterFormat = generateTextFromSpreadsheet(JSON.parse(text))
                        console.log(betterFormat)
                        setRawContext(betterFormat)
                    })
            } else {
                fetch(dataUrl)
                    .then((res) => res.text())
                    .then((text) => {
                        setRawContext(text)
                        setAppSpaceContent(text)
                        setType("text")
                    })
            }
        })()
    }, [activeAppSpace, appSpaces, setRawContext])

    const handleEditorChange = () => {
        setUnsavedChanges(true)
        setRawContext(editorRef.current.getContent())
        debouncedSaveChanges()
    }

    return (
        <div
            style={{
                display:
                    appSpaces?.length > 0 && activeSession.view === "chat" ? undefined : "none",
            }}
            className={styles.AppSpace}>
            {appSpaceLoading && (
                <div className={styles.loading}>
                    <div className="loader"></div>
                    Loading file...
                </div>
            )}
            {type === "pdf" && (
                <iframe
                    src={appSpaceContent}
                    onLoad={() => setAppSpaceLoading(false)}
                    width={"100%"}
                    height={"100%"}
                    title="pdf"
                    style={{ border: "none" }}
                />
            )}
            {type === "image" && (
                <div className={styles.imageViewer}>
                    <img
                        src={appSpaceContent}
                        onLoad={() => setAppSpaceLoading(false)}
                        onError={() => setAppSpaceLoading(false)}
                        style={{ width: "100%", height: "auto" }}
                        alt="viewer"
                    />
                </div>
            )}
            {type === "sheet" && (
                <Excel
                    setRawContext={setRawContext}
                    appSpaceContent={appSpaceContent}
                    activeAppSpace={activeAppSpace}
                    onLoad={() => setAppSpaceLoading(false)}
                />
            )}
            {type === "text" && (
                <div
                    style={{
                        height: "100%",
                        pointerEvents:
                            activeAppSpace.fileId === "french" ||
                            activeAppSpace.fileId === "russian"
                                ? "none"
                                : "auto",
                    }}>
                    <Editor
                        onInit={(evt: any, editor: any) => {
                            editorRef.current = editor
                            setAppSpaceLoading(false)
                        }}
                        // onLoadContent={() => {}}
                        initialValue={appSpaceContent}
                        apiKey="0zy4qv4meksyt7oin8lzvenoxzv12sgxfb1fwhnedck1neff"
                        onKeyPress={handleEditorChange}
                        init={{
                            height: "100%",
                            menubar: true,
                            // plugins: [
                            //     "advlist autolink lists link image charmap print preview anchor",
                            //     "searchreplace visualblocks code fullscreen",
                            //     "insertdatetime media table paste code help wordcount",
                            // ],
                            toolbar:
                                "undo redo | formatselect | " +
                                "bold italic backcolor | alignleft aligncenter " +
                                "alignright alignjustify | bullist numlist outdent indent | " +
                                "removeformat",
                            content_style:
                                "body { font-family:Helvetica,Arial,sans-serif; font-size:16px }",
                        }}
                    />
                </div>
            )}
        </div>
    )
}

export default AppSpace
